import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import type { PackagePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Pencil, Trash2 } from "lucide-react";
import { ListBackButton } from "@/components/list-back-button";

const statusBadge: Record<string, string> = {
  draft: "rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground",
  active: "rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400",
  archived: "rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
};

export default function PackageDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState<PackagePublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api
      .getPackage(id)
      .then((res) => setPkg(res.package))
      .catch((e) =>
        setError(e instanceof api.ApiError ? e.message : "Failed to load package"),
      )
      .finally(() => setLoading(false));
  }, [id]);

  async function handleDelete() {
    if (!id || !confirm("Delete this package?")) return;
    try {
      await api.deletePackage(id);
      navigate("/packages");
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to delete package");
    }
  }

  if (loading) return <p className="text-muted-foreground" data-page="package-detail" data-state="loading">Loading...</p>;
  if (!pkg) return <p className="text-sm text-destructive" data-page="package-detail" data-state="error" data-error>{error ?? "Package not found"}</p>;

  return (
    <div className="flex flex-col gap-6" data-page="package-detail" data-state={error ? "error" : "ready"}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-region="header">
        <div>
          <ListBackButton to="/packages" label="Packages" />
          <h1 className="text-2xl font-semibold">{pkg.name}</h1>
          <div className="flex items-center gap-3 text-muted-foreground">
            <span>{pkg.organization_name}</span>
            <span className={statusBadge[pkg.status] ?? statusBadge.draft}>
              {pkg.status}
            </span>
            <span>{pkg.placement_count} placements</span>
          </div>
          {pkg.description && (
            <p className="mt-1 text-sm text-muted-foreground">{pkg.description}</p>
          )}
        </div>
        <div className="flex gap-2">
          {pkg._actions.includes("edit") && (
            <Link
              to={`/packages/${pkg.id}/edit`}
              className={buttonVariants({ variant: "outline", size: "sm" })}
              data-action="edit"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Link>
          )}
          {pkg._actions.includes("delete") && (
            <Button variant="outline" size="sm" onClick={handleDelete} data-action="delete">
              <Trash2 className="mr-2 h-4 w-4 text-destructive" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
